import { Image, Typography } from '@/components/atoms'
import { config } from '@/config'
import { ThemeColor } from '@/enums'
import { isHubspotChatLoaded, openHubspotChat, themeColor } from '@/utils'
import { Badge, Card } from '@surein/ui'
import dayjs from 'dayjs'
import { ChevronRight, Mail, MessageSquare, Phone } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { ifProp } from 'styled-tools'
import { Column, Row } from '../../styled'
import { ModalBase } from '../ModalBase'
import { ReactNode } from 'react'

const StyledGreenDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${themeColor(ThemeColor.success1)};
`

const StyledCard = styled(Card)<{ onClick: () => void }>`
  padding: 16px;
  border-radius: 16px;
  background-color: ${themeColor(ThemeColor.b10)};
  cursor: ${ifProp('onClick', 'pointer', 'default')};

  svg {
    width: 16px;
    height: 16px;
    color: ${themeColor(ThemeColor.electric500)};
  }
`

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  padding: 16px;
  border-radius: 50%;
  background-color: ${themeColor(ThemeColor.info3)};
`

interface ContactModalEntryProps {
  hidden?: boolean
  icon: ReactNode
  text: string
  subtitle?: string | ReactNode
  onClick?: () => void
}

const ContactModalEntry = ({
  hidden,
  icon,
  text,
  subtitle,
  onClick,
}: ContactModalEntryProps) => {
  if (hidden) return null

  return (
    <StyledCard onClick={onClick} variant="borderless">
      <Row justifyContent="space-between">
        <Row alignItems="center" gap="16px">
          <IconWrapper>{icon}</IconWrapper>
          <Column gap="4px">
            <Typography variant="p2Body">{text}</Typography>
            {typeof subtitle === 'string' ? (
              <Typography color={ThemeColor.b50} variant="badgeText">
                {subtitle}
              </Typography>
            ) : (
              subtitle
            )}
          </Column>
        </Row>
        {onClick && <ChevronRight />}
      </Row>
    </StyledCard>
  )
}

export interface ContactModalProps {
  isOpen: boolean
  onClose: () => void
  hasHeadquarters?: boolean
}

export const ContactModal = ({
  isOpen,
  onClose,
  hasHeadquarters,
}: ContactModalProps) => {
  const { t } = useTranslation()
  const shouldShowChat = isHubspotChatLoaded()

  const configPhone = hasHeadquarters
    ? config.midMarketPhone
    : config.contactPhone

  const handleOpenChat = () => {
    openHubspotChat()
    onClose()
  }

  const isWorkingHours = dayjs().hour() >= 9 && dayjs().hour() < 20

  const entries = [
    {
      icon: <MessageSquare />,
      text: t('contactModal.liveChat'),
      hidden: !shouldShowChat,
      subtitle: (
        <Badge isRounded variant="info">
          <Typography bold color={ThemeColor.electric500} variant="badgeText">
            {isWorkingHours
              ? t('contactModal.averageResponseTime')
              : t('contactModal.workingHoursHint')}
          </Typography>
        </Badge>
      ),
      onClick: handleOpenChat,
    },
    {
      icon: <Phone />,
      text: t('contactModal.phone'),
      subtitle: configPhone,
    },
    {
      icon: <Mail />,
      text: t('contactModal.email'),
      subtitle: config.contactEmail,
    },
  ]

  return (
    <ModalBase closable isOpen={isOpen} onClose={onClose} padding="16px">
      <Column gap="20px">
        <Row alignItems="center" gap="12px">
          <Image name="chatblock" type="png" />
          <Column>
            <Typography bold variant="p1Body">
              Daniel
            </Typography>
            {isWorkingHours && (
              <Row alignItems="center" gap="8px">
                <StyledGreenDot />
                <Typography variant="p2Body">
                  {t('contactModal.online')}
                </Typography>
              </Row>
            )}
          </Column>
        </Row>
        <Column gap="4px">
          <Typography bold variant="h4">
            {t('contactModal.title')}
          </Typography>
          <Typography color={ThemeColor.b50} variant="p2Body">
            {t('contactModal.subtitle')}
          </Typography>
        </Column>
        {entries.map((entry) => (
          <ContactModalEntry key={entry.text} {...entry} />
        ))}
      </Column>
    </ModalBase>
  )
}
